import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Send, CheckCircle } from "lucide-react";

const CATEGORIES = [
  { value: "paiement", label: "Paiement" },
  { value: "transaction", label: "Transfert de billets" },
  { value: "compte", label: "Mon compte" },
  { value: "autre", label: "Autre" },
];

const SupportTicketForm = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [formData, setFormData] = useState({
    customer_email: "",
    customer_name: "",
    customer_phone: "",
    subject: "",
    description: "",
    category: "autre",
    priority: "48h",
    source: "form",
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.customer_name.trim()) newErrors.customer_name = "Le nom est requis";
    if (!formData.customer_email.trim()) {
      newErrors.customer_email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) {
      newErrors.customer_email = "Email invalide";
    }
    if (!formData.subject.trim()) newErrors.subject = "Le sujet est requis";
    if (!formData.description.trim()) {
      newErrors.description = "La description est requise";
    } else if (formData.description.trim().length < 20) {
      newErrors.description = "La description doit contenir au moins 20 caractères";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/support/tickets`, formData);
      setTicketId(response.data.ticket_id);
      setSubmitted(true);
      toast.success("Votre demande a été envoyée avec succès!");
      if (onSuccess) onSuccess(response.data);
    } catch (error) {
      console.error("Error submitting ticket:", error);
      toast.error("Erreur lors de l'envoi de votre demande. Veuillez réessayer.");
    }
    setLoading(false);
  };

  const handleClose = () => {
    setSubmitted(false);
    setTicketId(null);
    setFormData({
      customer_email: "",
      customer_name: "",
      customer_phone: "",
      subject: "",
      description: "",
      category: "autre",
      priority: "48h",
      source: "form",
    });
    setErrors({});
    onOpenChange(false);
  };

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Demande envoyée!
            </h3>
            <p className="text-gray-600 mb-4">
              Votre ticket de support a été créé avec succès.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-500">Numéro de référence</p>
              <p className="font-mono text-lg font-semibold text-brand-600">
                #{ticketId?.slice(0, 8).toUpperCase()}
              </p>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Notre équipe vous répondra dans les plus brefs délais à l'adresse {formData.customer_email}
            </p>
            <Button onClick={handleClose} className="w-full">
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nous contacter</DialogTitle>
          <DialogDescription>
            Remplissez le formulaire ci-dessous et notre équipe vous répondra rapidement.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="customer_name">Votre nom *</Label>
              <Input
                id="customer_name"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                placeholder="Jean Dupont"
                className={errors.customer_name ? "border-red-500" : ""}
                data-testid="ticket-form-name"
              />
              {errors.customer_name && (
                <p className="text-sm text-red-500">{errors.customer_name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="customer_email">Votre email *</Label>
              <Input
                id="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                placeholder="jean@exemple.com"
                className={errors.customer_email ? "border-red-500" : ""}
                data-testid="ticket-form-email"
              />
              {errors.customer_email && (
                <p className="text-sm text-red-500">{errors.customer_email}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="customer_phone">Téléphone (optionnel)</Label>
            <Input
              id="customer_phone"
              value={formData.customer_phone}
              onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
              placeholder="+1 514 123 4567"
              data-testid="ticket-form-phone"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Catégorie</Label>
            <Select 
              value={formData.category} 
              onValueChange={(v) => setFormData({ ...formData, category: v })}
            >
              <SelectTrigger data-testid="ticket-form-category">
                <SelectValue placeholder="Sélectionnez une catégorie" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Sujet *</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Objet de votre demande"
              className={errors.subject ? "border-red-500" : ""}
              data-testid="ticket-form-subject"
            />
            {errors.subject && (
              <p className="text-sm text-red-500">{errors.subject}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Décrivez votre problème ou question en détail..."
              rows={5}
              className={errors.description ? "border-red-500" : ""}
              data-testid="ticket-form-description"
            />
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description}</p>
            )}
            <p className="text-xs text-gray-500">
              {formData.description.length}/20 caractères minimum
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-brand-600 hover:bg-brand-700"
              disabled={loading}
              data-testid="ticket-form-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SupportTicketForm;
